<template>
    <div class="card">
        <div class="row">
            <div class="card-body" :class="productsClass">
                <div class="d-flex justify-content-between">
                    <h2>Devices</h2>
                    <div>
                        <button
                            v-if="!moreInfoProduct && !filterVisible"
                            :class="filterButtonClass"
                            class="btn btn-bg-white ml-2 float-right"
                            @click="toggleFilter"
                        >
                            <i class="fa fa-filter" />
                            &nbsp;Filter
                        </button>
                        <button
                            class="btn btn-bg-white ml-2 float-right"
                            @click="goToShoppingCart"
                        >
                            <i class="fa fa fa-shopping-cart" />
                            &nbsp;Cart
                        </button>
                    </div>
                </div>
                <hr />
                <Loading :loading="loading">
                    <div v-if="moreInfoProduct">
                        <div class="flex-fill mainPage row">
                            <DeviceProductInformation
                                :product="moreInfoProduct"
                            />

                            <p
                                class="text-danger text-center w-100"
                                v-if="!hasStock(moreInfoProduct)"
                            >
                                Not Available in Color:
                                <b>{{ moreInfoProduct.SelectedColorName }}</b>
                            </p>
                        </div>
                        <div
                            class="d-flex flex-row flex-wrap justify-content-end"
                        >
                            <div>
                                <button
                                    class="btn display-card-button secondary"
                                    @click="clearMoreInfoProduct"
                                >
                                    Back
                                </button>
                            </div>
                            <div class="ml-2">
                                <button
                                    class="btn display-card-button primary"
                                    :disabled="!hasStock(moreInfoProduct)"
                                    @click="
                                        buyMoreInfoProduct(moreInfoProduct.Id)
                                    "
                                >
                                    Buy Now
                                </button>
                            </div>
                        </div>
                    </div>
                    <div v-else>
                        <div v-if="hasProducts" class="row">
                            <ProductCard
                                v-for="product in products"
                                :id="product.Id"
                                :key="product.Id"
                                class="col-xl-3 col-md-4 col-sm-6 col-12"
                                call-to-action="Buy Now"
                                selector-for="Device"
                                :product="product"
                                :color="product.SelectedColorName"
                                :free-text-cta="true"
                                :header-text="product.Name"
                                @select="selectDevice"
                                @moreInfo="setMoreInfoProduct"
                            >
                                <div v-if="hasImages(product)">
                                    <ProductImageWithColorSelector
                                        v-if="hasColors(product)"
                                        :initialProduct="product"
                                        @color-selected="updateSelectedColor"
                                    />
                                    <ProductImage v-else :product="product" />
                                </div>
                            </ProductCard>
                        </div>
                        <NoProductsCard v-else class="flex-fill mainPage row" />
                        <div
                            v-if="hasProducts && hasPages"
                            class="row d-flex justify-content-center"
                        >
                            <Paginate
                                v-model="pageNumber"
                                :page-count="totalPages"
                                :page-range="3"
                                :margin-pages="1"
                                :click-handler="updatePageNumber"
                                :prev-text="'Prev'"
                                next-text="Next"
                                container-class="pagination"
                                :page-class="'page-item'"
                                page-link-class="page-link"
                                next-link-class="page-link"
                                prev-link-class="page-link"
                            >
                            </Paginate>
                        </div>
                    </div>
                </Loading>
            </div>
            <div class="col-3">
                <DeviceFilter
                    v-if="filterVisible"
                    class="filter-height"
                    :minimum="0"
                    :maximum="maximumDevicePrice"
                    :allProducts="products"
                    :allBrands="allBrands"
                    :allCategories="allCategories"
                    :filterState="filterState"
                    @toggle-filter="toggleFilter"
                    @products-filtered="productsFiltered"
                />
            </div>
        </div>
    </div>
</template>

<script>
import ProductCard from "../../shared/components/ProductCard";
import actions from "../store/actions";
import { mapActions, mapGetters, mapState } from "vuex";
import { find, isEmpty, propEq, pathOr } from "ramda";
import Loading from "../../shared/components/Loading";
import ProductImage from "../../shared/components/ProductImage";
import ProductImageWithColorSelector from "../../shared/components/ProductImageWithColourSelector";
import DeviceProductInformation from "../components/DeviceProductInformation";
import DeviceFilter from "../../shared/components/DeviceFilter";
import NoProductsCard from "../../shared/components/NoProductsCard";
import Paginate from "vuejs-paginate-next";

export default {
    name: "DeviceDisplayShell",
    components: {
        Loading,
        ProductCard,
        ProductImage,
        DeviceFilter,
        NoProductsCard,
        DeviceProductInformation,
        ProductImageWithColorSelector,
        Paginate,
    },
    data() {
        return {
            moreInfoProduct: null,
            filterVisible: false,
        };
    },
    computed: {
        ...mapState([
            "loading",
            "products",
            "filterState",
            "productSummary",
            "pageNumber",
        ]),
        ...mapGetters([
            "totalPages",
            "maximumDevicePrice",
            "allBrands",
            "allCategories",
            "pageNumber",
        ]),
        productsClass: function () {
            return this.filterVisible ? "col-9" : "col-12";
        },
        filterButtonClass: function () {
            return this.filterVisible ? "active mr-3" : "";
        },
        hasProducts() {
            return this.products && this.products.length > 0;
        },
        hasPages() {
            return this.totalPages > 1;
        },
    },
    created() {
        this.getCategory().then(this.getProducts);
    },
    methods: {
        ...mapActions({
            getProducts: actions.GET_PRODUCTS,
            getCategory: actions.GET_CATEGORY,
            updatePageNumber: actions.UPDATE_PAGE_NUMBER,
            selectDevice: actions.SELECT_DEVICE,
            updateSelectedColor: actions.UPDATE_SELECTED_COLOR,
            setFilteredProducts: actions.SET_FILTERED_PRODUCTS,
            goToShoppingCart: actions.GOTO_SHOPPING_CART,
        }),
        hasImages(product) {
            return product.ProductImages && !isEmpty(product.ProductImages);
        },
        hasColors(product) {
            const colorAttribute =
                product.Attributes.find((f) => f.Name == "Color") || {};
            return (
                colorAttribute.ValueOptions &&
                colorAttribute.ValueOptions.length
            );
        },
        findProductById(id) {
            return find(propEq("Id", id))(this.products);
        },
        setMoreInfoProduct(id) {
            this.moreInfoProduct = this.findProductById(id);
        },
        buyMoreInfoProduct(id) {
            const device = this.findProductById(id);
            this.selectDevice(device);
        },
        clearMoreInfoProduct() {
            this.moreInfoProduct = null;
        },
        toggleFilter() {
            this.filterVisible = !this.filterVisible;
        },
        productsFiltered(filteredProducts) {
            this.setFilteredProducts(filteredProducts);
        },
        hasStock(moreInfoProduct) {
            if (!moreInfoProduct) return false;
            if (!this.hasColors(moreInfoProduct))
                return moreInfoProduct.StockLevels > 0;
            return pathOr(
                0,
                [
                    "AttributeStockLevels",
                    "Color",
                    moreInfoProduct.SelectedColorName,
                    "Quantity",
                ],
                moreInfoProduct
            );
        },
    },
};
</script>

<style lang="scss" scoped>
.filter-height {
    height: 100%;
}
</style>
